import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AssociateReport } from '@quorum/models/xs-resource';
import { ApiService } from '@quorum/api';
import { AssociateQueryParameters, LocationQueryParameters } from '@quorum/models/xs-query';
import { Address, Associate, Location, MessageType } from '@quorum/models/xs-resource';
import { createFormGroupFromObject } from 'libs/sha-services/src/lib/createFormGroupFromObject';
import { combineLatest, Observable, of, pipe, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { google } from 'google-maps';

@Component({
  selector: 'sha-create-associate-address',
  templateUrl: './create-associate-address.component.html',
  styleUrls: ['./create-associate-address.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateAssociateAddressComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('addressLine1', { read: MatAutocompleteTrigger }) addressAutocompleteTrigger: MatAutocompleteTrigger;
  @Input() associate: Associate;
  @Input() address: Address;
  @Input() associateDropdowns$: Observable<any>;
  @Input() addressDropdowns$: Observable<any>;
  @Output() resourcesEmitted: EventEmitter<{
    associate: Associate;
    address: Address;
  }> = new EventEmitter<{ associate: Associate; address: Address }>();
  associateForm: FormGroup;
  addressForm: FormGroup;
  filteredAssociates: AssociateReport[] = [];
  selectedStepIndex: number = 0;
  createReady: boolean = false;
  searchedAssociateName: string = '';
  dateMask = createAutoCorrectedDatePipe('mm/dd/yyyy');
  locations$: Subscription;
  selectedAssoc: AssociateReport;
  associateFormChangesSub: Subscription;
  addressFormChangesSub: Subscription;
  associateFormNameSub: Subscription;
  addressFormProvStateSub: Subscription;
  addressLineSub: Subscription;
  addressPredictions: any[] = [];
  placesService: any;
  phoneNumberMask = ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  filteredLocations$: Observable<Location[]>;
  associateFormCombinedSub: Subscription;

  constructor(private apiService: ApiService, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.associateFormNameSub = this.associateForm['controls'].name.valueChanges
      .pipe(
        filter((input: string) => input.length >= 3),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((value: any) => {
          let params: AssociateQueryParameters = new AssociateQueryParameters({
            search: value,
          });
          this.searchedAssociateName = value;
          return this.apiService.get<AssociateReport[]>('v/1/reporting/associates', { params: params }).pipe(
            map((associates) => {
              this.filteredAssociates = associates;
              this.associateForm
                .get('firstName')
                .setValue(
                  this.associateForm.get('name').value.split(',')[1]
                    ? this.associateForm.get('name').value.split(',')[1].trim()
                    : this.associateForm.get('name').value
                );
              this.associateForm
                .get('lastName')
                .setValue(
                  this.associateForm.get('name').value.split(',')[1]
                    ? this.associateForm.get('name').value.split(',')[0].trim()
                    : null
                );
            }),
            catchError((error) => {
              this.associateForm.enable({ emitEvent: false });
              return of(error);
            })
          );
        })
      )
      .subscribe();

    this.associateFormCombinedSub = combineLatest(
      this.associateDropdowns$,
      this.associateForm.get('messagePreferenceId').valueChanges
    )
      .pipe(
        map(([appointmentDropdowns, messagePreference]) => {
          return { messageTypes: appointmentDropdowns.messageTypes, messagePreference };
        })
      )
      .subscribe((combinedData) => {
        if (
          combinedData.messageTypes
            .find((messageType: MessageType) => messageType.id === combinedData.messagePreference)
            .description.toLowerCase() === 'email'
        ) {
          this.associateForm.get('emailAddress').setValidators([Validators.email, Validators.required]);
          this.associateForm.get('emailAddress').markAsTouched();
          this.associateForm.get('emailAddress').updateValueAndValidity();
          this.addressForm.get('cellPhoneNumber').clearValidators();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
        } else if (
          combinedData.messageTypes
            .find((messageType: MessageType) => messageType.id === combinedData.messagePreference)
            .description.toLowerCase() === 'text'
        ) {
          this.addressForm.get('cellPhoneNumber').setValidators(Validators.required);
          this.addressForm.get('cellPhoneNumber').markAsTouched();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
          this.associateForm.get('emailAddress').setValidators([Validators.email]);
          this.associateForm.get('emailAddress').updateValueAndValidity();
        } else {
          this.associateForm.get('emailAddress').setValidators([Validators.email]);
          this.associateForm.get('emailAddress').updateValueAndValidity();
          this.addressForm.get('cellPhoneNumber').clearValidators();
          this.addressForm.get('cellPhoneNumber').updateValueAndValidity();
        }
      });

    this.associateFormChangesSub = this.associateForm.valueChanges.subscribe((value) => {
      if (
        this.associateForm.valid &&
        !this.associateForm.disabled &&
        this.addressForm.valid &&
        !this.addressForm.disabled
      ) {
        let address = this.addressForm.getRawValue();
        let updatedAddress: Address = {
          ...address,
          locationId: address.locationId.id,
          cellPhoneNumber: address.cellPhoneNumber ? address.cellPhoneNumber.replace(/\D+/g, '') : null,
          homePhoneNumber: address.homePhoneNumber ? address.homePhoneNumber.replace(/\D+/g, '') : null,
        };
        this.resourcesEmitted.emit({ associate: this.associateForm.getRawValue(), address: updatedAddress });
      }
    });

    this.addressFormChangesSub = this.addressForm.valueChanges.subscribe((value) => {
      if (
        this.associateForm.valid &&
        !this.associateForm.disabled &&
        this.addressForm.valid &&
        !this.addressForm.disabled
      ) {
        let address = this.addressForm.getRawValue();
        let updatedAddress: Address = {
          ...address,
          locationId: address.locationId.id ? address.locationId.id : address.locationId,
          cellPhoneNumber: address.cellPhoneNumber ? address.cellPhoneNumber.replace(/\D+/g, '') : null,
          homePhoneNumber: address.homePhoneNumber ? address.homePhoneNumber.replace(/\D+/g, '') : null,
        };
        this.resourcesEmitted.emit({ associate: this.associateForm.getRawValue(), address: updatedAddress });
      }
    });

    this.addressFormProvStateSub = this.addressForm
      .get('provStateId')
      .valueChanges.pipe(
        tap((provState) => {
          console.log(`prov state changed`);
          this.addressForm.patchValue({
            locationId: '',
            countryId: provState.countryId,
          });
        }),
        switchMap((provState) => {
          let params: LocationQueryParameters = new LocationQueryParameters({
            provStateId: provState,
            pageSize: 2000,
          });
          return this.apiService.get<Location[]>('v/1/associates/locations', { params: params }).pipe(
            switchMap((locations) => {
              this.filteredLocations$ = this.addressForm.get('locationId').valueChanges.pipe(
                startWith(
                  this.selectedAssoc
                    ? locations.find((location: Location) => location.id === this.selectedAssoc.locationId)
                    : ''
                ),
                map((value) => {
                  if (typeof value === 'string') {
                    return locations.filter((option: any) =>
                      option.description.toLowerCase().includes(value.toLowerCase())
                    );
                  } else {
                    return locations.filter((option: any) => option.id === value.id);
                  }
                }),
                tap((locations: Location[]) => {
                  if (this.selectedAssoc) {
                    this.addressForm.patchValue(
                      { locationId: locations.find((loco) => loco.id === this.selectedAssoc.locationId) },
                      { emitEvent: false }
                    );
                  } else {
                    this.addressForm.get('locationId').enable({ emitEvent: false });
                  }
                })
              );
              return of(null);
            })
          );
        })
      )
      .subscribe((provState: any) => {});

    this.addressLineSub = this.addressForm
      .get('addressLine1')
      .valueChanges.pipe(
        filter((input: string) => {
          if (input) {
            return input.length >= 4;
          }
        }),
        debounceTime(500),
        distinctUntilChanged(),
        map((value) => {
          this.getAutocompletePredictions(value);
        })
      )
      .subscribe(() => {});
  }

  ngOnDestroy() {
    this.associateFormChangesSub.unsubscribe();
    this.addressFormChangesSub.unsubscribe();
    this.associateFormNameSub.unsubscribe();
    this.addressFormProvStateSub.unsubscribe();
    this.associateFormCombinedSub.unsubscribe();
  }

  getAutocompletePredictions(searchedAddressValue: string) {
    let autocompleteService = new google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions(
      {
        types: ['address'],
        input: searchedAddressValue,
        componentRestrictions: { country: ['ca', 'us'] },
      },
      (predictions: any, status: any) => {
        if (status == google.maps.places.PlacesServiceStatus.OK) {
          this.addressPredictions = predictions;
          this.addressAutocompleteTrigger.openPanel();
        } else {
          console.log(status, 'error in address predictions');
        }
      }
    );
  }

  selectAddress(selectedAddress: any, addressDropdowns: any) {
    let addressField = document.querySelector('#addressLine1') as HTMLInputElement;
    this.placesService = new google.maps.places.PlacesService(addressField);

    this.placesService.getDetails(
      { placeId: selectedAddress.place_id, fields: ['address_components'] },
      (result: any, status: any) => {
        let address: any = {
          addressLine1: '',
          provStateId: '',
          locationName: '',
          postalZipCode: '',
          countryId: '',
          dropdownsLocation: null,
          location: null,
        };

        for (const component of result.address_components) {
          const componentType = component.types[0];

          switch (componentType) {
            case 'country': {
              address.countryId = component.short_name === 'CA' ? 'CAN' : component.short_name === 'US' ? 'USA' : null;

              this.addressForm
                .get('countryId')
                .patchValue(component.short_name === 'CA' ? 'CAN' : component.short_name === 'US' ? 'USA' : null, {
                  emitEvent: false,
                });
              break;
            }

            case 'street_number': {
              address.addressLine1 = `${component.long_name} `;
              break;
            }

            case 'route': {
              address.addressLine1 += component.short_name;
              break;
            }

            case 'locality': {
              address.locationName = component.long_name;
              break;
            }

            case 'postal_code_prefix': {
              address.postalZipCode = component.long_name;
              break;
            }

            case 'postal_code': {
              address.postalZipCode = component.long_name;
              break;
            }

            case 'postal_code_suffix': {
              address.postalZipCode = `${address.postalZipCode}-${component.long_name}`;
              break;
            }

            case 'administrative_area_level_1': {
              this.addressForm.get('locationId').enable({ emitEvent: false });

              this.addressForm
                .get('provStateId')
                .patchValue(component.short_name === 'QC' ? 'PQ' : component.short_name, {
                  emitEvent: false,
                });

              address.provStateId = component.short_name === 'QC' ? 'PQ' : component.short_name;
              break;
            }
          }
        }

        if (address.locationName) {
          let selectedLocation: Location = addressDropdowns.locations.filter((option: Location): any => {
            return (
              option.description.toLowerCase().includes(address.locationName.toLowerCase()) &&
              option.provStateId.toLowerCase().includes(address.provStateId.toLowerCase())
            );
          });

          if (selectedLocation[0]) {
            this.addressForm.get('locationId').patchValue(selectedLocation[0], { emitEvent: false });
            this.addressForm.get('countryId').patchValue(selectedLocation[0].countryId, { emitEvent: false });
            address.dropdownsLocation = selectedLocation[0];
          } else {
            this.addressForm.get('locationId').patchValue(null);
          }
        }

        this.addressForm.get('addressLine1').patchValue(address.addressLine1, { emitEvent: false });
        this.addressForm.get('postalZipCode').patchValue(address.postalZipCode, { emitEvent: false });

        if (address.dropdownsLocation) {
          this.addressForm.get('locationId').disable();
        }

        this.addressPredictions = [];
      }
    );
  }

  disableAssociateForm() {
    Object.keys(this.associateForm.getRawValue()).forEach((key) => {
      if (key !== 'name') {
        this.associateForm.get(key).disable({ emitEvent: false });
      }
    });
  }
  disableAddressForm() {
    Object.keys(this.addressForm.getRawValue()).forEach((key) => {
      this.addressForm.get(key).disable({ emitEvent: false });
    });
  }

  enableAssociateForm() {
    Object.keys(this.associateForm.getRawValue()).forEach((key) => {
      if (key !== 'name') {
        this.associateForm.get(key).enable({ emitEvent: false });
      }
    });
  }
  enableAddressForm() {
    Object.keys(this.addressForm.getRawValue()).forEach((key) => {
      this.addressForm.get(key).enable({ emitEvent: false });
    });
  }

  setAssociateRequiredFields() {
    this.associateForm.get('name').setValidators(Validators.required);
    this.associateForm.get('messagePreferenceId').setValidators(Validators.required);
    this.associateForm.get('emailAddress').setValidators(Validators.email);
  }

  setAddressRequiredFields() {
    this.addressForm.get('addressLine1').setValidators(Validators.required);
    this.addressForm.get('locationId').setValidators(Validators.required);
    this.addressForm.get('provStateId').setValidators(Validators.required);
    this.addressForm.get('locationId').disable({ emitEvent: false, onlySelf: true });
  }

  // createVehicle(associate: Associate, address: any, vehicle: Vehicle) {
  //   let updatedAddress: Address = {
  //     ...address,
  //     locationId: address.locationId.id,
  //     cellPhoneNumber: address.cellPhoneNumber ? address.cellPhoneNumber.replace(/\D+/g, '') : null,
  //     homePhoneNumber: address.homePhoneNumber ? address.homePhoneNumber.replace(/\D+/g, '') : null
  //   };
  //   this.createEmitted.emit({ associate: associate, address: updatedAddress, vehicle: vehicle });
  // }

  setPostalCodeAndProvince(location: Location) {
    this.addressForm.patchValue({
      location: location,
      postalZipCode: location.postalCode,
      countryId: location.countryId,
    });
    console.log('set postal code and province');
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.associate) {
      this.associateForm = createFormGroupFromObject(changes.associate.currentValue);
      this.associateForm.addControl('name', new FormControl(''));
      this.setAssociateRequiredFields();
      this.associateForm.markAllAsTouched();
      this.disableAssociateForm();
    }
    if (changes.address) {
      console.log('changes address');
      this.addressForm = createFormGroupFromObject(changes.address.currentValue);
      this.setAddressRequiredFields();
      this.addressForm.markAllAsTouched();
      this.disableAddressForm();
    }
  }

  selectAssociate(value: any, filteredAssociates?: AssociateReport[]) {
    if (value == 'addAssociate') {
      this.selectedAssoc = null;
      this.associateForm.get('name').setValue(this.searchedAssociateName);
      this.enableAddressForm();
      this.enableAssociateForm();
    } else {
      if (filteredAssociates) {
        let selectedAssociate = filteredAssociates.find((associate) => associate.id == value);
        this.selectedAssoc = selectedAssociate;
        this.associateForm.patchValue(
          {
            ...this.associateForm,
            emailAddress: `${selectedAssociate.emailAddress}`,
            classId: selectedAssociate.classId,
            messagePreferenceId: selectedAssociate.messagePreferenceId,
          },
          { emitEvent: false }
        );
        this.disableAssociateForm();
        this.addressForm.patchValue({
          ...this.addressForm,
          addressLine1: selectedAssociate.addressLine1,
          homePhoneNumber: selectedAssociate.homePhoneNumber,
          cellPhoneNumber: selectedAssociate.cellPhoneNumber,
          countryId: selectedAssociate.countryId,
          postalZipCode: selectedAssociate.postalZipCode,
          provStateId: selectedAssociate.provStateId,
        });
        this.disableAddressForm();
      }
    }

    this.filteredAssociates = [];
  }

  displayAssociateName(filteredAssociates: AssociateReport[], value: string) {
    let displayAssociate: AssociateReport;
    if (filteredAssociates.length > 0) {
      displayAssociate = filteredAssociates.find((associate: AssociateReport) => associate.id === value);
      if (displayAssociate) {
        return displayAssociate.firstName
          ? `${displayAssociate.lastName}, ${displayAssociate.firstName}`
          : `${displayAssociate.fullName}`;
      } else {
        return value;
      }
    } else {
      return value;
    }
  }

  displayLocationName(location: Location): string {
    return location && location.description
      ? location.description + ' ' + location.provStateId + ', ' + location.countryId
      : '';
  }

  makeUpperCase(str: string) {
    if (str) {
      str = str[0].toUpperCase() + str.slice(1);
      str = str.replace(/,\s*([a-z])/g, function (d, e) {
        return ', ' + e.toUpperCase();
      });
      str = str.replace(/'\s*([a-z])/g, function (d, e) {
        return "'" + e.toUpperCase();
      });
      return str;
    }
    return '';
  }
}
